import { useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { describeError, pictogramUrl } from "../../api/http"
import { keys } from "../../api/keys"
import { pictogramWrites } from "../../api/mutations"
import { useGroupPictograms, usePictograms } from "../../api/queries"
import type { ProductGroup } from "../../api/types"
import type { GroupRights } from "../../auth/rights"
import { ActionNotes, Note } from "../../components/Note"
import { useAction } from "../../hooks/useAction"

/**
 * Пиктограммы группы — значки свойств товара: сталь, покрытие, назначение.
 * На сайте это `oc_product_option`, в печатном каталоге — иконки под карточкой.
 *
 * Показываются всем, правятся не всеми. Бэк делит по принадлежности группы
 * (`PictogramControllerAuthHelper`): латвийские ведут `ContentManagerLv` и
 * `ContentManagerRg`, российские и ничейные — только `ContentManagerRF` или
 * фотограф. Значит в РФ-группах Рига пиктограммы видит, но не меняет — панель
 * так и говорит, вместо того чтобы получать `401` по кнопке.
 */
export default function PictogramsPanel({
	group,
	languageId,
	rights,
}: {
	group: ProductGroup
	languageId: number
	rights: GroupRights
}) {
	const client = useQueryClient()
	const all = usePictograms(languageId)
	const current = useGroupPictograms(group.id, languageId)

	const action = useAction({
		after: () =>
			client.invalidateQueries({ queryKey: keys.groupPictograms(group.id) }),
	})

	const [picked, setPicked] = useState<number | null>(null)

	const rows = useMemo(
		() => [...(current.data ?? [])].sort((a, b) => a.sort - b.sort),
		[current.data]
	)

	/** Свободные пиктограммы: те, что группе ещё не назначены. */
	const free = useMemo(() => {
		const taken = new Set(rows.map(item => item.id))
		return (all.data ?? [])
			.filter(item => !taken.has(item.id))
			.sort((a, b) => a.sort - b.sort)
	}, [all.data, rows])

	const busy = action.busy || current.isFetching

	function add() {
		if (picked === null) return
		const id = picked
		setPicked(null)
		void action.run(() => pictogramWrites.add(group.id, id))
	}

	return (
		<div className="panel">
			<div className="panel__head">
				<span>Пиктограммы{rows.length > 0 && ` · ${rows.length}`}</span>
				<span className="panel__spacer" />
				{!rights.pictograms && (
					<span className="muted">только просмотр</span>
				)}
			</div>

			<div className="panel__body">
				<ActionNotes action={action} />

				{current.isError && (
					<Note tone="error">
						Не удалось получить пиктограммы: {describeError(current.error)}
					</Note>
				)}

				{current.isLoading && <span className="spinner" />}

				{!current.isLoading && rows.length === 0 && (
					<div className="muted">Пиктограмм нет</div>
				)}

				{rows.length > 0 && (
					<ul className="picto">
						{rows.map(item => (
							<li className="picto__item" key={item.id} title={item.name}>
								<img
									className="picto__img"
									src={pictogramUrl(item.imageUrl)}
									alt={item.name}
									loading="lazy"
								/>
								<span className="picto__name">{item.name}</span>
								{rights.pictograms && (
									<button
										className="cat-mini cat-mini--danger picto__remove"
										disabled={busy}
										title="Убрать пиктограмму у группы"
										onClick={() =>
											void action.run(() =>
												pictogramWrites.remove(group.id, item.id)
											)
										}
									>
										✕
									</button>
								)}
							</li>
						))}
					</ul>
				)}

				{rights.pictograms && (
					<div className="picto__add">
						<select
							className="select"
							disabled={busy || free.length === 0}
							value={picked ?? ""}
							onChange={event =>
								setPicked(event.target.value ? Number(event.target.value) : null)
							}
						>
							<option value="">
								{free.length === 0 ? "— все уже добавлены —" : "— пиктограмма —"}
							</option>
							{free.map(item => (
								<option key={item.id} value={item.id}>
									{item.name}
								</option>
							))}
						</select>

						<button
							className="btn"
							disabled={busy || picked === null}
							onClick={add}
						>
							Добавить
						</button>

						{busy && <span className="spinner" />}
					</div>
				)}
			</div>
		</div>
	)
}
